
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Users, Target, AlertCircle } from 'lucide-react';

export function UserBehaviorInsights() {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['user-behavior-insights'],
    queryFn: async () => {
      // Get all subscribers
      const { data: subscribers } = await supabase
        .from('subscribers')
        .select('id, user_id, email, subscribed, is_trial');

      if (!subscribers) return null;

      // Get users approaching limits
      const usersApproachingLimits = await Promise.all(
        subscribers
          .filter(sub => !sub.subscribed)
          .map(async (subscriber) => {
            const { count: projectsCount } = await supabase
              .from('projects')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', subscriber.user_id);

            // For now, set clients count to 0 since everything is free
            const clientsCount = 0;

            return {
              email: subscriber.email,
              projects_count: projectsCount || 0,
              clients_count: clientsCount || 0,
              user_id: subscriber.user_id
            };
          })
      );

      // Filter users at or near limits
      const atProjectLimit = usersApproachingLimits.filter(user => user.projects_count >= 3);
      const atClientLimit = usersApproachingLimits.filter(user => user.clients_count >= 3);

      // Get most active users (by time entries)
      const { data: activeUsers } = await supabase
        .from('time_entries')
        .select('user_id')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      const userActivity = activeUsers?.reduce((acc: any, entry) => {
        acc[entry.user_id] = (acc[entry.user_id] || 0) + 1;
        return acc;
      }, {}) || {};

      const topActiveUsers = Object.entries(userActivity)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .slice(0, 5);

      // Get user emails for active users
      const activeUserEmails = await Promise.all(
        topActiveUsers.map(async ([userId, count]) => {
          const { data: subscriber } = await supabase
            .from('subscribers')
            .select('email')
            .eq('user_id', userId)
            .maybeSingle();

          return {
            email: subscriber?.email || 'Unknown',
            entries: count as number
          };
        })
      );

      return {
        usersAtProjectLimit: atProjectLimit.length,
        usersAtClientLimit: atClientLimit.length,
        projectLimitUsers: atProjectLimit.slice(0, 5),
        clientLimitUsers: atClientLimit.slice(0, 5),
        topActiveUsers: activeUserEmails
      };
    }
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Behavior Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading insights...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">At Project Limit</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {insights?.usersAtProjectLimit || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Users with 3/3 projects
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">At Client Limit</CardTitle>
            <Target className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {insights?.usersAtClientLimit || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Users with 3/3 clients
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Opportunities</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {((insights?.usersAtProjectLimit || 0) + (insights?.usersAtClientLimit || 0))}
            </div>
            <p className="text-xs text-muted-foreground">
              Prime for upgrade
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Users at Project Limit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {insights?.projectLimitUsers?.map((user, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                  <span className="text-sm font-medium">{user.email}</span>
                  <Badge variant="outline" className="text-amber-700">
                    {user.projects_count}/3 projects
                  </Badge>
                </div>
              )) || (
                <p className="text-sm text-gray-500">No users at project limit</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Most Active Users (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {insights?.topActiveUsers?.map((user, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                  <span className="text-sm font-medium">{user.email}</span>
                  <Badge variant="outline" className="text-blue-700">
                    {user.entries} entries
                  </Badge>
                </div>
              )) || (
                <p className="text-sm text-gray-500">No activity data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
